/**
 * Movement system. Reads an entity's Intent (a desired direction + throttle),
 * converts size into a max speed (with the post-shrink boost applied), eases
 * velocity toward the target, integrates position, and clamps to the arena.
 */
import type { GameConfig } from '../../config/config';
import { sizeToSpeed } from '../formulas';
import { add, clamp, lerp, normalize, scale } from '../math/vec2';
import type { Entity } from '../types';

export function applyMovement(e: Entity, cfg: GameConfig, dt: number): void {
  const boost = e.boostTimer > 0 ? cfg.actions.shrink.boostMultiplier : 1;
  const maxSpeed = sizeToSpeed(e.size, cfg) * boost;

  // moveDir magnitude is an analog throttle in [0, 1]; direction is the heading.
  const throttle = clamp(Math.hypot(e.intent.moveDir.x, e.intent.moveDir.y), 0, 1);
  const dir = normalize(e.intent.moveDir);
  const desired = scale(dir, maxSpeed * throttle);

  const a = clamp(cfg.movement.acceleration * dt, 0, 1);
  e.vel = lerp(e.vel, desired, a);
  e.pos = add(e.pos, scale(e.vel, dt));

  // Keep the whole circle inside the arena; kill velocity into the wall.
  const r = e.size;
  if (e.pos.x < r) {
    e.pos.x = r;
    if (e.vel.x < 0) e.vel.x = 0;
  } else if (e.pos.x > cfg.world.width - r) {
    e.pos.x = cfg.world.width - r;
    if (e.vel.x > 0) e.vel.x = 0;
  }
  if (e.pos.y < r) {
    e.pos.y = r;
    if (e.vel.y < 0) e.vel.y = 0;
  } else if (e.pos.y > cfg.world.height - r) {
    e.pos.y = cfg.world.height - r;
    if (e.vel.y > 0) e.vel.y = 0;
  }
}
